import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trash2 } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import type { AdminFontRow } from "@listto/shared";

interface FontCardProps {
  font: AdminFontRow;
  onToggleActive: (id: string, active: boolean) => void;
  onDeleteConfirm: (id: string) => void;
  t: (s: string | MessageDescriptor, v?: Record<string, unknown>) => string;
}

export function FontCard(props: FontCardProps) {
  const handleToggleChange = (v: boolean) => {
    props.onToggleActive(props.font.id, v);
  };

  const handleDeleteConfirm = () => {
    props.onDeleteConfirm(props.font.id);
  };

  return (
    <Card class="overflow-hidden group">
      <CardContent class="p-4 flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div class="h-12 w-12 rounded bg-muted flex items-center justify-center text-2xl font-serif">
            Aa
          </div>
          <div>
            <h3 class="font-bold text-lg">{props.font.family}</h3>
            <p class="text-xs text-muted-foreground">
              {props.font.category} •{" "}
              {(() => {
                const count = props.font.variants.length;
                return props.t(msg`${count} variants`);
              })()}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <div class="flex flex-col items-end gap-1">
            <span class="text-[10px] uppercase font-bold text-muted-foreground">
              {props.t(msg`Active`)}
            </span>
            <Switch checked={props.font.isActive} onCheckedChange={handleToggleChange} />
          </div>
          <ConfirmDialog
            title={props.t(msg`Delete font?`)}
            description={(() => {
              const family = props.font.family;
              return props.t(
                msg`Are you sure you want to delete the font "${family}"? This action cannot be undone.`,
              );
            })()}
            confirmLabel={props.t(msg`Delete`)}
            variant="destructive"
            onConfirm={handleDeleteConfirm}
            renderTrigger={(open) => (
              <Button
                variant="ghost"
                size="icon"
                class="text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={open}
              >
                <Trash2 class="h-4 w-4" />
              </Button>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}
